import { assign, createMachine } from 'xstate';
import { uniqBy } from 'lodash';
import { addDays, addMinutes, format, subDays, startOfDay, endOfDay } from 'date-fns';
import { Mitarbeiter, Niederlassung, Rolle, Abwesenheit, Funktion } from '~/gql/ucpw/graphql';
import { getDaysInInterval } from '~/pages/projects/utils';

type ScheduleTypes = 'day' | 'week';

export type CalendarContext = {
    isEmergencyService: boolean;
    emergencyEvents: any[];
    scheduleType: ScheduleTypes;
    events: any[];
    absences: Abwesenheit[];
    mbsc: any[];
    employees: Mitarbeiter[];
    roleId: undefined | null | number;
    functionId: undefined | null | number;
    employeeId?: undefined | null | number;
    viewerEmployeeId?: undefined | null | number;
    subproject?: undefined | null | Record<string, any>;
    subprojectId?: undefined | null | number;
    roles: Rolle[];
    resourceIndex: number;
    employeeFunctions: Funktion[];
    locationState?: any;
    branchId?: number;
    branches: Niederlassung[];
    created?: any[];
    updated?: any[];
    deleted?: any[];
    selectedDate: Date;
    from: Date;
    until: Date;
};

export const context: CalendarContext = {
    isEmergencyService: false,
    emergencyEvents: [],
    scheduleType: 'day',
    resourceIndex: 0,
    events: [],
    absences: [],
    mbsc: [],
    employeeId: null,
    employees: [],
    roleId: null,
    functionId: null,
    roles: [],
    employeeFunctions: [],
    branches: [],
    created: [],
    updated: [],
    deleted: [],
    selectedDate: new Date(format(new Date(), 'yyyy-MM-dd')),
    from: subDays(new Date(format(new Date(), 'yyyy-MM-dd')), 0),
    until: addDays(new Date(format(new Date(), 'yyyy-MM-dd')), 1),
};

export const calendarMachine =
    /** @xstate-layout N4IgpgJg5mDOIC5QGMCGAbMA7CqBOAxAMIASAggHIDiAogPoBCASpaQNoAMAuoqAA4B7WAEsALsIFZeIAJ6IAtACYAbIoB0AVgA0IAB6INARgDsagCyLDADmMaAvnZ1pMOfAQDKNACp13AVQYABSYAeQApGiIfAEkAEU4eJBBBEXFJaTkEeQBmDQ4dfQQrDQBONWMrQ0V7RxBnbFxCUkpaOlCAGRoE6RSxCSkkzJy8gsRisoqqmqcMBrdm6npPTqiaWLpYsi8u7h6hPvTBhTNs0209REMTUzNlG2m62dcm8kXfGhXt9c3ttkNE-j7NIDUBDDTVUYIQzKZQacoPerPAheEJUKidOg0ACyNCYtAoRAAmt0kr1gRkFMoqppIZYrNk1CZrLYHDMXI01MIsGICBBJGA1LBRKhRALERyuWISYDUv1pIULOpFCU7rYdENsoYOJpWY92fhOdzRGoYKIGHhUFhkAALOBkHA0AC2fHQAhkYDgagARharbbYLz+YaAG4CADWYqeEqNJrAZt9NrtDudrvdnp9lsTsAQXNDaGBCWlySBcqShXkFhKhk0hhV2UUHGVFXViAqaisKmyJSrJUU1Q4JQRUYNkuNpvNmf99ogTpdbo9sDUYBT87ggawAtz4cj+rwhrEsfjk6TM5XacXy7n55zWDzIv6hd2pJLh1A5YsHfbZmMtmMjZKzayIg2Tah2yhdj2fZ5CUty6uKI4xuOCZTsmV4LkuwbYKIAZ8huIbbmo8F7qOh4Tn6J6zqm6FgJhWDYTed4FtwRZkqWb7HCBpj0pY9b-oBmTWAyJQcGY4JmLWUEDtkcHDsRiFxmRWbTpRq6LqgXqwNgyBrrhm63gRRH7mOCnIRRZ7oepmlWnADECPmD7MU+MoHCCFxZB+VjtuB2QdnxVgtggihWJ5JRgWYkH9iqMm7kZh5MAImCLngCU6UGW4RoRsmxaa8WJWoyWJbZ9mSI+ALFrKr5uRWmpwg2xiGLxTb+UBCBtmBEESZFQ4xZeVGpXh6U7nMe69apRX3iVjllaxlXvsoxhmGoyiNrcC2KPVygBaFDIcLte37btijRcNGFYThaX6Rlhk0Wd41MVwLEvq5c3dpoVh3BYFgbQFWrBWoHCTP2B1HbUhmWVp-V6aGV1ZeD1nZluxVYKVewVc9xx3NqVJWAOihfdCP21gyhgaKoeQHY2x3PGoADuqA8iiaIYtiuL4kSj1o-KxyVGUuR4-N+ObS11gnJoFPA1THJ0zyCytMwrAkBzLlc+5PNqHzq2Cz9g7VhogPk-tINsid0uiMQrytO4JBrH4GJeISgQ7NNT0qxWasawL60Ey1DalEtpPVJLBqm+bLRLB8kRfBsWxO6jytltztbq2JnvfT7VTqCTZNB3uIdK+SCeq0nHtrWnmR9iBaiKNkJwsqDWWwAArl6fDJQAVmAyBm7p+EwzFTct+3neiHdDkPU55Xx+x7l3HCK013jXtC+X1fatXtfdSdA+twIHdd+uUMGQ3zc73vI+IxNyPMf8ccF9PFaz-9eML1rPvZLk6tTA4tRYAIEBwNIIit82JVTyEYZO-NS7eyGFSdQ4Vuw4yzmYDgpwc5GWAbNY4f4zgAV-H5AK8hDBmEWiYXIVIqgcA0KTDQZg0EkSQseWAylzIAOfJzQuD8-alB-HrfBLV5CkyWsYXIOMVTiXBMYEodD5JHnIkw1CfVFwZjkRg9GM8riaFwbwpqAU2qdm7J1aCm9qb0JMow5haFPSjXPKo12twNHcLwTon2y11b6IikYI2eoTqmNkUpBRqlTp0XgGwqeVV7G6y0X+ZxmRiHVnagY3s1QqjSIPAwuRFjFFqDhtpEJzk77hPIZonh0SALNUyKoBk9ZwKJKgik+uMVfGKRQqeSxi4m7IFyXkyeBS5oOKiXwzIONFoJI8fU42JiZG5VYfkkBc0uEDJiQoTy80REDmUOI9aUiGk+KmSlJK+zbEcIicUpxZTdGeVGYYgcxjoxpLjNM9pjdOlwG6TNNRD9+klMGYgFQ2pqkdSSZ4tB1iFxHPvuTKpKcoHL0uJQ25BobrBPBaA3aULIGvwEtXZQYtxZ7S8WDDSEM3kuw4ZCiBmsl4-XEp5PFhs0GmxReWclJdMWXFKGUA2eKCXH0HrvYeTKFAsuhWyqEwjFp0vxd-OwQA */
    createMachine<CalendarContext>(
        {
            id: 'calendar',
            initial: 'init',
            context,
            // edictableActionArguments: true,
            states: {
                init: {
                    type: 'parallel',
                    states: {
                        getBranchesAndEmployees: {
                            initial: 'branches',
                            states: {
                                branches: {
                                    invoke: {
                                        src: 'fetchBranches',
                                        onDone: {
                                            target: 'employees',
                                            actions: assign((ctx, { data }) => {
                                                return {
                                                    branches: data?.branches,
                                                    branchId: data?.branchId
                                                        ? data?.branchId
                                                        : data?.[0]?.id,
                                                };
                                            }),
                                        },
                                    },
                                },
                                employees: {
                                    invoke: {
                                        src: 'fetchEmployees',
                                        onDone: {
                                            target: 'events',
                                            actions: 'setEmployees',
                                        },
                                    },
                                },
                                events: {
                                    invoke: {
                                        src: 'fetchEvents',
                                        onDone: {
                                            target: 'absences',
                                            actions: assign((_, { data: events }) => ({ events })),
                                        },
                                        // onError: {
                                        //     target: 'events',
                                        // },
                                    },
                                },
                                absences: {
                                    invoke: {
                                        src: 'fetchAbsences',
                                        onDone: {
                                            target: 'success',
                                            actions: ['setAbsences', 'generateMobiscrollEvents'],
                                        },
                                    },
                                },
                                success: {
                                    type: 'final',
                                },
                            },
                        },
                        getRoles: {
                            initial: 'roles',
                            states: {
                                roles: {
                                    invoke: {
                                        src: 'fetchRoles',
                                        onDone: {
                                            target: 'success',
                                            actions: assign((_, { data: roles = [] }) => ({
                                                roles,
                                            })),
                                        },
                                    },
                                },
                                success: { type: 'final' },
                            },
                        },
                        getFunctions: {
                            initial: 'functions',
                            states: {
                                functions: {
                                    invoke: {
                                        src: 'fetchFunctions',
                                        onDone: {
                                            target: 'success',
                                            actions: assign(
                                                (_, { data: employeeFunctions = [] }) => ({
                                                    employeeFunctions,
                                                })
                                            ),
                                        },
                                    },
                                },
                                success: { type: 'final' },
                            },
                        },
                    },
                    onDone: 'wait',
                },
                employees: {
                    invoke: {
                        src: 'fetchEmployees',
                        onDone: {
                            target: 'events',
                            actions: 'setEmployees',
                        },
                    },
                },
                events: {
                    invoke: {
                        src: 'fetchEvents',
                        onDone: {
                            target: 'absences',
                            actions: assign((_, { data: events }) => ({ events })),
                        },
                    },
                },
                createEvent: {
                    invoke: {
                        src: 'createEvent',
                        onDone: {
                            target: 'events',
                            actions: assign(({ created = [] }, { data }) => {
                                return {
                                    created: [...created, data],
                                };
                            }),
                        },
                    },
                },
                updateEvent: {
                    invoke: {
                        src: 'updateEvent',
                        onDone: {
                            target: 'events',
                            actions: [
                                assign(({ updated = [] }, data) => {
                                    return {
                                        updated: [...updated, data],
                                    };
                                }),
                            ],
                        },
                    },
                },
                deleteEvent: {
                    invoke: {
                        src: 'deleteEvent',
                        onDone: {
                            target: 'events',
                            actions: assign(({ deleted = [] }, { data }) => {
                                return {
                                    deleted: [...deleted, data],
                                };
                            }),
                        },
                    },
                },
                absences: {
                    invoke: {
                        src: 'fetchAbsences',
                        onDone: {
                            target: 'wait',
                            actions: ['setAbsences', 'generateMobiscrollEvents'],
                        },
                    },
                },
                wait: {
                    always: [
                        {
                            target: 'subproject',
                            cond: ({ subprojectId, subproject }) =>
                                Boolean(subprojectId) && !Boolean(subproject),
                        },
                    ],
                    on: {
                        TOGGLE_EMERGENCY: {
                            actions: 'toggleEmergency',
                        },
                        SET_EMERGENCY_EVENTS: {
                            actions: 'setEmergencyEvents',
                        },
                        CHANGE_BRANCH: {
                            target: 'employees',
                            actions: 'onChange',
                        },
                        CREATE_EVENT: 'createEvent',
                        UPDATE_EVENT: 'updateEvent',
                        DELETE_EVENT: 'deleteEvent',
                        CHANGE_EMPLOYEE: {
                            target: 'events',
                            actions: 'onChange',
                        },
                        CHANGE_SCHEDULE_TYPE: {
                            target: 'events',
                            actions: 'changeScheduleType',
                        },
                        CHANGE_SELECTED_DATE: {
                            target: 'events',
                            actions: 'setDate',
                        },
                        SET_RESOURCE_INDEX: {
                            actions: 'onChange',
                        },
                    },
                },
                subproject: {
                    invoke: {
                        src: 'getSubproject',
                        onDone: [
                            {
                                cond: ({ branchId }, { data }) =>
                                    data?.projekt?.niederlassungId === branchId,
                                target: 'wait',
                                actions: 'setSubproject',
                            },
                            {
                                target: 'employees',
                                actions: 'setSubproject',
                            },
                        ],
                    },
                },
            },
            on: {
                CHANGE_BRANCH: {
                    target: 'employees',
                    actions: 'onChange',
                },
                SET_LOCATION_STATE: {
                    actions: assign((_, { locationState }) => ({
                        locationState,
                        ...(locationState?.branchId && { branchId: locationState?.branchId }),
                    })),
                },
                SET_SUBPROJECT_ID: {
                    actions: 'setSubprojectId',
                },
                CHANGE_ROLE: {
                    target: 'employees',
                    actions: 'onChange',
                },
                CHANGE_FUNCTION: {
                    target: 'employees',
                    actions: 'onChange',
                },
                CHANGE_EMPLOYEE: {
                    target: 'events',
                    actions: 'onChange',
                },
                CHANGE_SCHEDULE_TYPE: {
                    target: 'events',
                    actions: 'changeScheduleType',
                },
                CHANGE_SELECTED_DATE: [
                    {
                        target: 'init',
                        actions: 'setDate',
                        cond: ({ roles, branches }) => !(roles.length && branches.length), // don't break init but also not overfetch branches + roles
                    },
                    { target: 'events', actions: 'setDate' },
                ],
                TOGGLE_EMERGENCY: {
                    actions: 'toggleEmergency',
                },
                SET_EMERGENCY_EVENTS: {
                    actions: 'setEmergencyEvents',
                },
                SET_RESOURCE_INDEX: {
                    actions: 'onChange',
                },
            },
        },
        {
            actions: {
                changeScheduleType: assign(
                    ({ locationState, viewerEmployeeId }, { scheduleType }) => ({
                        scheduleType,
                        ...(viewerEmployeeId && { employeeId: viewerEmployeeId }),
                        ...(locationState && {
                            employeeId: locationState?.mitarbeiterIdSachbearbeiter,
                        }),
                    })
                ),
                onChange: assign((_, { context = {} }) => context),
                setEmployees: assign((ctx, { data: employees = [] }) => ({
                    employees,
                    employeeId: employees?.find((e: any) => e?.id === ctx?.viewerEmployeeId)
                        ? ctx?.viewerEmployeeId
                        : employees?.[0]?.id,
                })),
                setSubprojectId: assign((_, { value: subprojectId }) => ({
                    subprojectId,
                })),
                setSubproject: assign((_, { data: subproject = null }) => ({
                    subproject,
                    ...(subproject && {
                        branchId: subproject?.projekt?.niederlassungId,
                    }),
                })),
                setDate: assign((ctx, { value: selectedDate }) => ({
                    selectedDate: new Date(selectedDate),
                    from: startOfDay(
                        subDays(new Date(selectedDate), ctx.scheduleType === 'day' ? 1 : 5)
                    ),
                    until: endOfDay(
                        addDays(new Date(selectedDate), ctx.scheduleType === 'day' ? 1 : 5)
                    ),
                })),
                generateMobiscrollEvents: assign(({ events, absences }) => ({
                    mbsc: events.reduce?.((acc, item) => {
                        return [
                            ...acc,
                            {
                                allDay: false,
                                title: item?.typSnippet?.name || item?.__typename,
                                start: item?.geplant,
                                end: addMinutes(new Date(item?.geplant), item?.dauer || 30),
                                color: item?.sachbearbeiter?.farbe || 'blackAlpha.300',
                                resource: item?.mitarbeiterIdSachbearbeiter,
                                // ...(item?.erledigt && { editable: false }),
                                ...item,
                            },
                        ];
                    }, absences),
                })),
                setAbsences: assign((_, { data: absences = [] }) => ({
                    absences: absences?.reduce((acc: any, absence: any) => {
                        const daysOfIntervall = getDaysInInterval({
                            start: new Date(absence.datumFrom),
                            end: new Date(absence.datumUntil),
                        });

                        const intervall = daysOfIntervall?.map((d) => {
                            const day = new Date(d);
                            return {
                                start: new Date(
                                    day.getFullYear(),
                                    day.getMonth(),
                                    day.getDate(),
                                    0
                                ),
                                end: new Date(day.getFullYear(), day.getMonth(), day.getDate(), 24),
                                resource: absence.mitarbeiterId,
                                ...absence,
                                uniqId: [absence.mitarbeiterId, day]?.join(''),
                                title: 'Abwesenheit',
                                /**
                                 * https://demo.mobiscroll.com/scheduler/move-resize-drag-drop-to-create-events
                                 * ⚠️ Events can be marked as fixed by setting their editable property to false.
                                 */
                                editable: false,
                            };
                        });

                        return [...acc, ...uniqBy(intervall, 'uniqId')];
                    }, []),
                })),
                toggleEmergency: assign(({ isEmergencyService }) => ({
                    isEmergencyService: !isEmergencyService,
                })),
                setEmergencyEvents: assign((ctx, { emergencyEvents }) => ({
                    emergencyEvents,
                    isEmergencyService: Boolean(emergencyEvents?.length),
                })),
            },
        }
    );
